import DeviceGroupSettings from 'components/DeviceGroupSettings/DeviceGroupSettings';
import DeviceSettings from 'components/DeviceSettings/DeviceSettings';
import BaseModal from 'components/Modals/BaseModal';
import AwareFloodDeviceSettings from 'features/integrations/features/ConnectedDevices/components/DeviceSettings';
import USGSDeviceSettings from 'features/integrations/features/ConnectedDevices/components/USGSDeviceSettings';
import {
  selectAwareOpen,
  selectDeviceSettings,
  selectGroupSettings,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
  selectSelectedGroupId,
  selectUSGSOpen,
} from 'features/integrations/state/selectors/deviceSelectors';
import {
  clearDeviceSettings,
  clearGroupSettings,
  openDeviceSettings,
  setAwareOpen,
  setUSGSOpen,
} from 'features/integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/integrations/state/thunks';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { BsChevronLeft } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';

export default function DeviceSettingsModals() {
  const dispatch = useDispatch();
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const awareOpen = useSelector(selectAwareOpen);
  const usgsOpen = useSelector(selectUSGSOpen);
  const deviceSettings = useSelector(selectDeviceSettings);
  const groupSettings = useSelector(selectGroupSettings);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const selectedGroupId = useSelector(selectSelectedGroupId);

  // Resolve the potential device from Redux settings OR the currently selected ID
  const potentialDevice =
    deviceSettings || connectedDevices?.find((device) => device.id === selectedDeviceId) || null;

  // Strict Check: If this is an AWARE or USGS device, we return NULL here.
  //    This ensures the Standard Modal (below) ignores it completely,
  //    preventing the "double modal" issue.
  const resolvedDeviceSettings =
    DeviceIsSystemType(potentialDevice, 'Flood') || potentialDevice?.system_type === 'USGS'
      ? null
      : potentialDevice;

  const resolvedGroupSettings =
    groupSettings || deviceGroups?.find((group) => group.id === selectedGroupId) || null;
  const awareDevice = connectedDevices?.find((device) => device.id === selectedDeviceId) || null;
  const usgsDevice = connectedDevices?.find((device) => device.id === selectedDeviceId) || null;

  useEffect(() => {
    if (!awareOpen || !selectedDeviceId) return;

    const alreadySelectedId = connectedDevices?.[selectedDeviceIndex]?.id;
    if (alreadySelectedId === selectedDeviceId) return;

    dispatch(setSelectedDevice({ index: -1, id: selectedDeviceId }));
  }, [awareOpen, selectedDeviceId, selectedDeviceIndex, connectedDevices, dispatch]);

  const onSelectFromGroup = (id) => {
    dispatch(setSelectedDevice({ index: -1, id }));
    const device = connectedDevices?.find((item) => item.id === id);

    // Safety check: ensure we don't open standard settings for an AWARE device from a group list either
    if (device && !DeviceIsSystemType(device, 'Flood')) {
      dispatch(openDeviceSettings(device.id));
    }
  };

  return (
    <>
      {/* Group Modal */}
      <BaseModal
        show={!!resolvedGroupSettings && !resolvedDeviceSettings && !awareOpen && !usgsOpen}
        onHide={() => dispatch(clearGroupSettings())}
        title={resolvedGroupSettings?.name}
        centered
      >
        {resolvedGroupSettings && (
          <DeviceGroupSettings
            group={resolvedGroupSettings}
            onSelectDevice={onSelectFromGroup}
            onClose={() => dispatch(clearGroupSettings())}
          />
        )}
      </BaseModal>

      {/* Standard Device Modal */}
      <BaseModal
        show={!!resolvedDeviceSettings && !awareOpen && !usgsOpen}
        onHide={() => dispatch(clearDeviceSettings())}
        title={resolvedDeviceSettings?.alias}
        centered
      >
        {resolvedGroupSettings && (
          <Button variant="link" className="px-0" onClick={() => dispatch(clearDeviceSettings())}>
            <BsChevronLeft /> Back
          </Button>
        )}

        {resolvedDeviceSettings && (
          <DeviceSettings device={resolvedDeviceSettings} onSelectDevice={onSelectFromGroup} />
        )}
      </BaseModal>

      {/* Aware Device Modal */}
      <BaseModal
        show={awareOpen}
        onHide={() => dispatch(setAwareOpen(false))}
        centered
        title={
          awareDevice
            ? `${awareDevice.static_id ? `${awareDevice.static_id} ` : ''}${
                awareDevice.alias
              } (${awareDevice.node_id})`
            : 'Device Settings'
        }
      >
        <AwareFloodDeviceSettings />
      </BaseModal>

      {/* USGS Device Modal */}
      <BaseModal
        show={usgsOpen}
        onHide={() => dispatch(setUSGSOpen(false))}
        centered
        title={
          usgsDevice
            ? `${usgsDevice.alias || 'USGS Monitoring Station'} (${usgsDevice.node_id})`
            : 'USGS Settings'
        }
      >
        <USGSDeviceSettings onClose={() => dispatch(setUSGSOpen(false))} />
      </BaseModal>
    </>
  );
}
